/**
 * Sprint M5.23 (29.07.2026) Phase 6 — Upgrade-Modal.
 *
 * Launch-Blocker L-2 (02.09.2026):
 *   Pre-Launch-Warteliste-Flow entfernt. „Jetzt upgraden" führt jetzt DIREKT
 *   auf den Ablefy-Checkout (aus `/api/pricing/pro-solo` → checkout_urls).
 *   Falls Frank die URL nicht liefert, fallback auf White-Label-Workspace.
 *
 * Cross-Chat-Hinweis (Briefing v1.4, 02.09. 21:00):
 *   Dieser Fix steht bereits unter der neuen Leitregel v1.4 („Fachbegriff
 *   führt, Bildsprache erklärt, Handlungsbegriff aktiviert"). Im Sprint
 *   v1.0.1.2 Block B (Terminologie-Refactor, Mi 09.09.) NICHT nochmal
 *   anfassen. Siehe /app/memory/PRE_REFACTOR_AUDIT_v1_4.md.
 */
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

const PLANS = [
  {
    id: "monatsplan",
    apiKey: "monthly",
    label: "Monatsplan",
    price: "39 €",
    period: "/ Monat",
    sub: "Monatlich kündbar.",
  },
  {
    id: "jahresplan",
    apiKey: "yearly",
    label: "Jahresplan",
    price: "349 €",
    period: "/ Jahr",
    sub: "2 Monate gratis · jederzeit kündbar.",
    badge: "Empfohlen",
  },
];

const WHITELABEL_FALLBACK =
  process.env.REACT_APP_ABLEFY_WORKSPACE_URL ||
  "https://inside.effektiv-kommunizieren.de/s/AJUNG/sign_in";

export default function UpgradeModal({ open, onClose }) {
  const [selected, setSelected] = useState("jahresplan");
  const [checkoutUrls, setCheckoutUrls] = useState({ monthly: "", yearly: "" });

  // Launch-Blocker L-2: Pricing-Endpoint enthält jetzt die Ablefy-Checkout-URLs.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api
      .get("/pricing/pro-solo")
      .then((r) => {
        if (cancelled) return;
        const urls = r.data?.checkout_urls || {};
        setCheckoutUrls({
          monthly: urls.monthly || "",
          yearly: urls.yearly || "",
        });
      })
      .catch(() => {
        // fail-open: leerer URL-State → Fallback greift beim Klick
      });
    return () => { cancelled = true; };
  }, [open]);

  if (!open) return null;

  function handleUpgrade() {
    const plan = PLANS.find((p) => p.id === selected);
    if (!plan) return;
    const url = checkoutUrls[plan.apiKey] || WHITELABEL_FALLBACK;
    // Sichtbarer Redirect zum Ablefy-Checkout — kein Warteliste-State mehr.
    window.location.href = url;
  }

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        data-testid="upgrade-modal-backdrop"
        onClick={onClose}
      >
        <motion.div
          className="w-full max-w-lg rounded-2xl bg-white shadow-2xl"
          initial={{ scale: 0.94, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.96, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          data-testid="upgrade-modal"
        >
          <div className="flex items-start justify-between p-5 border-b border-slate-100">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-[#4563BB]">
                Pro Solo
              </p>
              <h2 className="mt-1 text-xl font-extrabold text-slate-900" data-testid="upgrade-modal-title">
                Wähle deinen Plan.
              </h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-slate-400 hover:text-slate-800 -mt-1 -mr-1 p-1"
              data-testid="upgrade-modal-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-5 space-y-4">
            <div className="space-y-2">
              {PLANS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setSelected(p.id)}
                  className={`w-full text-left rounded-xl border p-4 flex items-center gap-3 transition-colors ${
                    selected === p.id
                      ? "border-[#4563BB] bg-[#4563BB]/5"
                      : "border-slate-200 bg-white hover:border-slate-400"
                  }`}
                  data-testid={`upgrade-modal-plan-${p.id}`}
                >
                  <div className={`w-4 h-4 rounded-full border-2 ${
                    selected === p.id ? "border-[#4563BB] bg-[#4563BB]" : "border-slate-300"
                  }`} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-extrabold text-slate-900">{p.label}</p>
                      {p.badge && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-800 text-[10px] font-bold uppercase tracking-wider">
                          {p.badge}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 mt-0.5">{p.sub}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-extrabold text-slate-900">{p.price}</p>
                    <p className="text-xs text-slate-500">{p.period}</p>
                  </div>
                </button>
              ))}
            </div>

            {/* Launch-Blocker L-2 (02.09.2026): href zum Ablefy-Checkout,
                nicht mehr auf Warteliste-Endpoint. */}
            <a
              href={
                (checkoutUrls[PLANS.find((p) => p.id === selected)?.apiKey || "yearly"]) ||
                WHITELABEL_FALLBACK
              }
              onClick={(e) => {
                if (!selected) {
                  e.preventDefault();
                  return;
                }
                handleUpgrade();
                e.preventDefault();
              }}
              className={`w-full inline-flex items-center justify-center gap-1.5 rounded-xl bg-[#4563BB] hover:bg-[#3a52a3] text-white font-bold px-4 py-3 ${
                !selected ? "opacity-50 pointer-events-none" : ""
              }`}
              data-testid="upgrade-modal-submit"
              aria-disabled={!selected}
            >
              Jetzt upgraden
              <ArrowRight className="w-4 h-4" />
            </a>
            <p className="text-[11px] text-slate-500 text-center">
              Sichere Bezahlung über Ablefy · Jederzeit kündbar.
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
